import { cloneDeep, omit } from "lodash";
import { replace } from "redux-first-history";
import { call, put, race, select, take, takeLatest, takeLeading, type SagaReturnType } from "redux-saga/effects";

import { iotEventCampaignUpdate } from "@ds/modules/iot/redux/actions";
import { toastShowErrorAction, toastShowSuccessAction } from "@ds/modules/notifications/redux/actions";
import { isIotTableEntityAddedOrDeleted, selectTableData } from "@ds/modules/table-data/redux/selectors";
import { DEFAULT_SORT_FIELD } from "@ds/modules/table-data/redux/slice";
import { convertToApiQueryInfo } from "@ds/modules/table-data/utils/common";
import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";
import { campaignItemsActions, CampaignItemsRemoteOperation } from "@ds/modules/content/campaign-items/redux/slice";
import { campaignItemsService } from "@ds/modules/content/campaign-items/utils/api";
import { isApiCampaignItemQueryOutput } from "@ds/modules/content/campaign-items/utils/model";
import { normalize as normalizeItems } from "@ds/modules/content/campaign-items/utils/normalizer";

import { UnexpectedError } from "@ds/utils/errors";
import { logger } from "@ds/utils/logger";
import { UNLIMITED_PAGINATION } from "@ds/utils/query";
import { takeLatestOrEvery } from "@ds/utils/saga-helpers";
import { isErrorLike } from "@ds/utils/type-guards/error-guards";

import { campaignsService } from "../utils/api";
import { normalize } from "../utils/normalizer";
import { selectCampaignById, selectCampaignsByIds, selectTableCampaigns } from "./selectors";
import { CampaignsRemoteOperation, campaignsActions } from "./slice";

function* fetchCampaigns({ meta }: ReturnType<typeof campaignsActions.fetchCampaigns>) {
  try {
    const { sorting, pagination, queryInfo, isFetchedAlready } = selectTableData(
      yield select(),
      meta.options.tableType || MainTableDataTypeEnum.ContentCampaigns,
      undefined,
      meta.filters,
    );

    const newMeta = cloneDeep(meta);
    const resultFromCache: QueryOutput<Campaign> = { items: [] };

    if (!newMeta.options.cache?.disableCache) {
      if (isFetchedAlready && newMeta.options.tableType) {
        resultFromCache.items = selectTableCampaigns(yield select(), newMeta.options.tableType);
        newMeta.options.cache = {
          ...newMeta.options.cache,
          fetchedFromCache: true,
        };
      } else if (newMeta.filters.id && Object.keys(newMeta.filters).length === 1) {
        resultFromCache.items = selectCampaignsByIds(yield select(), newMeta.filters.id);
        if (resultFromCache.items.length === newMeta.filters.id.length) {
          newMeta.options.cache = {
            ...newMeta.options.cache,
            fetchedFromCache: true,
          };
        }
      }
    }

    const paginationToUse = newMeta.options.pagination ?? pagination;
    let result: QueryOutput<Campaign> = newMeta.options.cache?.fetchedFromCache
      ? resultFromCache
      : yield call(
          [campaignsService, campaignsService.getCampaigns],
          convertToApiQueryInfo(sorting, queryInfo),
          newMeta.options.tableType ? paginationToUse : UNLIMITED_PAGINATION,
        );

    result = newMeta.options.cache?.fetchedFromCache ? result : normalize(result);
    result = newMeta.options.tableType ? result : { items: result.items };

    yield put(campaignsActions.fetchCampaignsSucceeded(result, newMeta));
  } catch (err) {
    const errorTitle = "Fetch campaigns";
    if (isErrorLike(err)) {
      yield put(campaignsActions.fetchCampaignsFailed(meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* fetchCampaign({ payload, meta }: ReturnType<typeof campaignsActions.fetchCampaign>) {
  try {
    const newMeta = cloneDeep(meta);
    let result = selectCampaignById(yield select(), payload);
    if (result && !newMeta.options.cache?.disableCache) {
      newMeta.options.cache = {
        ...newMeta.options.cache,
        fetchedFromCache: true,
      };
    } else {
      const fetched: SagaReturnType<typeof campaignsService.getCampaign> = yield call(
        [campaignsService, campaignsService.getCampaign],
        payload,
      );
      result = normalize(fetched);
    }

    yield put(campaignsActions.fetchCampaignSucceeded(result, newMeta));
  } catch (err) {
    const errorTitle = "Fetch Campaign";
    if (isErrorLike(err)) {
      yield put(campaignsActions.fetchCampaignFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* createCampaign({ payload, meta }: ReturnType<typeof campaignsActions.createCampaign>) {
  try {
    const result: SagaReturnType<typeof campaignsService.createCampaign> = yield call(
      [campaignsService, campaignsService.createCampaign],
      payload,
    );

    const normalizedResult = normalize(result);
    yield put(campaignsActions.createCampaignSucceeded(normalizedResult, meta));

    const { sorting, pagination } = selectTableData(yield select(), MainTableDataTypeEnum.ContentCampaigns);

    // Navigate to the page where the new campaign appears based on the current sort.
    // Default sort is id desc → newest campaign (highest ID) is first → page 1.
    // For id asc → newest campaign is last → compute from total.
    let targetPage = 1;
    if (sorting.sortField === DEFAULT_SORT_FIELD && sorting.sortOrder === "asc") {
      const total = (pagination.total ?? 0) + 1;
      targetPage = Math.max(1, Math.ceil(total / pagination.pageSize));
    }

    yield put(toastShowSuccessAction("Campaign was created successfully"));
    yield put(campaignsActions.selectCampaigns([normalizedResult]));
    yield put(
      campaignsActions.fetchCampaigns(
        undefined,
        {
          tableType: MainTableDataTypeEnum.ContentCampaigns,
          cache: { disableCache: true },
          pagination: { pageNumber: targetPage, pageSize: pagination.pageSize },
        },
        CampaignsRemoteOperation.IOT_FETCH,
      ),
    );
  } catch (err) {
    const errorTitle = "Create campaign";
    if (isErrorLike(err)) {
      yield put(campaignsActions.createCampaignFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* duplicateCampaign({ payload, meta }: ReturnType<typeof campaignsActions.duplicateCampaign>) {
  try {
    const target = selectCampaignById(yield select(), payload.campaignId);
    if (!target) throw new Error("Target campaign not found");

    const cloned: Campaign = {
      ...omit(target, ["id"]),
      id: 0,
      name: payload.newTitle ?? `${target.name} (Copy)`,
    };

    const result: SagaReturnType<typeof campaignsService.createCampaign> = yield call(
      [campaignsService, campaignsService.createCampaign],
      cloned,
    );

    const newCampaignId: number = (result as unknown as { id: number }).id;

    const itemsResult: SagaReturnType<typeof campaignItemsService.getCampaignItems> = yield call(
      [campaignItemsService, campaignItemsService.getCampaignItems],
      { filters: { campaign_id: payload.campaignId } } as QueryInfo,
      UNLIMITED_PAGINATION,
    );

    const sortedItems = [...itemsResult.items].sort((a, b) => a.campaign_order - b.campaign_order);
    for (const item of sortedItems) {
      yield call([campaignItemsService, campaignItemsService.createCampaignItem], {
        ...omit(item, ["id", "playlist"]),
        id: 0,
        campaign_id: newCampaignId,
        campaign_order: -1,
      });
    }

    const normalizedResult = normalize({
      ...cloned,
      id: newCampaignId,
      campaign_items_count: sortedItems.length,
    } as Campaign);

    // Pre-fetch the new campaign's items so playlist_name is populated before navigation.
    const newItemsResult: SagaReturnType<typeof campaignItemsService.getCampaignItems> = yield call(
      [campaignItemsService, campaignItemsService.getCampaignItems],
      { filters: { campaign_id: newCampaignId } } as QueryInfo,
      UNLIMITED_PAGINATION,
    );
    const normalizedItems = isApiCampaignItemQueryOutput(newItemsResult)
      ? normalizeItems(newItemsResult)
      : newItemsResult;
    yield put(
      campaignItemsActions.fetchCampaignItemsSucceeded(normalizedItems, {
        remoteOperation: CampaignItemsRemoteOperation.CRUD,
        filters: { campaignId: newCampaignId },
        options: { tableType: DetailsTableDataTypeEnum.ContentCampaignItems },
      }),
    );

    yield put(campaignsActions.duplicateCampaignSucceeded(normalizedResult, meta));
    yield put(toastShowSuccessAction("Campaign was duplicated successfully"));
    yield put(campaignsActions.selectCampaigns([normalizedResult]));
    yield put(
      campaignsActions.fetchCampaigns(
        undefined,
        { tableType: MainTableDataTypeEnum.ContentCampaigns, cache: { disableCache: true } },
        CampaignsRemoteOperation.IOT_FETCH,
      ),
    );
  } catch (err) {
    const errorTitle = "Duplicate campaign";
    if (isErrorLike(err)) {
      yield put(campaignsActions.duplicateCampaignFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* updateCampaigns({ payload }: ReturnType<typeof campaignsActions.updateCampaigns>) {
  try {
    const result: SagaReturnType<typeof campaignsService.updateCampaigns> = yield call(
      [campaignsService, campaignsService.updateCampaigns],
      payload.ids,
      payload.data,
    );

    yield put(campaignsActions.updateCampaignsSucceeded(normalize(result)));
    yield put(toastShowSuccessAction("Campaign(s) were updated successfully"));
  } catch (err) {
    const errorTitle = "Update campaign(s)";
    if (isErrorLike(err)) {
      yield put(campaignsActions.updateCampaignsFailed(payload, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* deleteCampaigns({ payload, meta }: ReturnType<typeof campaignsActions.deleteCampaigns>) {
  try {
    yield call([campaignsService, campaignsService.deleteCampaigns], payload);
    yield put(campaignsActions.deleteCampaignsSucceeded(payload, meta));
    yield put(toastShowSuccessAction("Campaign(s) were deleted successfully"));
    yield put(
      campaignsActions.fetchCampaigns(
        undefined,
        { tableType: MainTableDataTypeEnum.ContentCampaigns, cache: { disableCache: true } },
        CampaignsRemoteOperation.IOT_FETCH,
      ),
    );

    if (meta.options?.redirectTo) {
      yield put(replace(meta.options.redirectTo));
    }
  } catch (err) {
    const errorTitle = "Delete campaign(s)";
    if (isErrorLike(err)) {
      yield put(campaignsActions.deleteCampaignsFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* refetchCampaigns({ payload }: ReturnType<typeof iotEventCampaignUpdate>) {
  const isAddedOrDeleted = isIotTableEntityAddedOrDeleted(
    yield select(),
    payload,
    MainTableDataTypeEnum.ContentCampaigns,
  );

  if (isAddedOrDeleted) {
    yield call(
      fetchCampaigns,
      campaignsActions.fetchCampaigns(
        undefined,
        {
          tableType: MainTableDataTypeEnum.ContentCampaigns,
          cache: { disableCache: true },
        },
        CampaignsRemoteOperation.IOT_FETCH,
      ),
    );
  }

  yield put(campaignsActions.iotUpdate(payload));
}

export function* watchCampaigns() {
  yield takeLatestOrEvery(campaignsActions.fetchCampaigns, function* (action) {
    yield race({
      task: call(fetchCampaigns, action),
      cancel: take([campaignsActions.selectCampaigns, campaignsActions.toggleSelected]),
    });
  });

  yield takeLatestOrEvery(campaignsActions.fetchCampaign, fetchCampaign);
  yield takeLeading(campaignsActions.createCampaign, createCampaign);
  yield takeLeading(campaignsActions.duplicateCampaign, duplicateCampaign);
  yield takeLeading(campaignsActions.updateCampaigns, updateCampaigns);
  yield takeLeading(campaignsActions.deleteCampaigns, deleteCampaigns);
  yield takeLatest(iotEventCampaignUpdate, refetchCampaigns);
}
